import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { SettingsCard } from "../SettingsCard";
import { apiFetch, apiRequest } from "../../../services/api.client";
import { API_BASE_URL } from "../../../constants";
import { useImportProgress } from "../../../contexts/ImportProgressContext";

// ── Types ───────────────────────────────────────────────────────────────────

type Entity = "patients" | "visits" | "prescriptions" | "appointments";
type Source = "csv" | "practo" | "lybrate" | "ekacare" | "docon" | "dently" | "kiwi_health" | "mydentist";
type Step = "source" | "mapping" | "dryrun" | "importing" | "done" | "history" | "merge-review";

interface RowError { row: number; reason: string }

interface DuplicateMatch {
  source_row: number;
  source_name: string;
  source_phone: string | null;
  source_dob: string | null;
  target_patient_id: string;
  target_name: string;
  target_phone: string | null;
  target_dob: string | null;
  match_reason: string;
  confidence: number;
}

interface DryRunResult {
  job_id: string;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  duplicate_rows: number;
  column_mapping: Record<string, string>;
  errors: RowError[];
  dedup_matches: DuplicateMatch[];
}

interface JobInfo {
  id: string;
  entity: string;
  source_type: string;
  file_name: string | null;
  status: string;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  duplicate_rows: number;
  created_rows: number;
  merged_rows: number;
  column_mapping: Record<string, string> | null;
  error_report: RowError[] | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  admin_id: string;
}

interface ZipUploadResult {
  job_id: string;
  status: string;
  processed: number;
  skipped: number;
  failed: number;
  validation_errors?: string[];
}

interface MergeProposal {
  id: string;
  clinic_id: string;
  import_job_id: string | null;
  source_patient_id: string;
  target_patient_id: string;
  match_reason: string;
  confidence: number;
  status: string;
  source_snapshot: Record<string, any> | null;
  target_snapshot: Record<string, any> | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const ENTITY_LABELS: Record<Entity, string> = {
  patients: "Patients",
  visits: "Visits",
  prescriptions: "Prescriptions",
  appointments: "Appointments",
};

const SOURCE_LABELS: Record<Source, string> = {
  csv: "Excel / CSV",
  practo: "Practo Export",
  lybrate: "Lybrate Export",
  ekacare: "ekaCare Export",
  docon: "DocOn Export",
  dently: "Dently Export",
  kiwi_health: "Kiwi Health Export",
  mydentist: "MyDentist Export",
};

const STATUS_COLORS: Record<string, string> = {
  completed: "bg-emerald-100 text-emerald-700",
  running: "bg-blue-100 text-blue-700",
  pending_async: "bg-blue-100 text-blue-700",
  validated: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  pending: "bg-gray-100 text-gray-500",
};

/** Build a toast-ready error message from a failed fetch response.
 *
 * The backend wraps HTTPException details in its standard envelope
 * ({"error": {"message": ...}}), not the default FastAPI {"detail": ...}
 * shape — reading err.detail (the old code) was always undefined, so every
 * failure here showed the generic fallback, not just the xlsx-parsing bug.
 * Also appends the x-request-id response header (set on every response by
 * the backend's request-id middleware) so support/on-call can correlate the
 * failure with CloudWatch logs without opening DevTools.
 */
async function formatApiError(resp: Response, fallback: string): Promise<string> {
  const requestId = resp.headers.get("x-request-id");
  let message = fallback;
  try {
    const body = await resp.json();
    message = body?.error?.message || body?.detail || fallback;
  } catch {
    // Non-JSON body — keep the fallback.
  }
  return requestId ? `${message} (ref: ${requestId})` : message;
}

const IMPORT_ORDER_HINT: Record<Entity, string | null> = {
  patients: null,
  visits: "Import patients first before importing visits.",
  prescriptions: "Import patients first before importing prescriptions.",
  appointments: "Patients and doctors must exist before importing appointments.",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Component ───────────────────────────────────────────────────────────────

export const DataImport: React.FC = () => {
  const location = useLocation();
  const initStep = (location.state as { importStep?: Step } | null)?.importStep || "source";
  const [step, setStep] = useState<Step>(initStep);
  const [entity, setEntity] = useState<Entity>("patients");
  const [source, setSource] = useState<Source>("csv");
  const [file, setFile] = useState<File | null>(null);
  const [autoMapping, setAutoMapping] = useState<Record<string, string | null>>({});
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [dryResult, setDryResult] = useState<DryRunResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<JobInfo | null>(null);
  const [jobs, setJobs] = useState<JobInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [quickImport, setQuickImport] = useState(false);
  const [depWarning, setDepWarning] = useState<string | null>(null);
  const [mergeProposals, setMergeProposals] = useState<MergeProposal[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeBusyId, setMergeBusyId] = useState<string | null>(null);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipUploading, setZipUploading] = useState(false);
  const [zipResult, setZipResult] = useState<ZipUploadResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const zipFileRef = useRef<HTMLInputElement>(null);
  const { activeImport, startTracking: trackImport } = useImportProgress();

  const KAAGAZY_FIELDS: Record<Entity, string[]> = {
    patients: ["first_name", "last_name", "full_name", "contact_number", "date_of_birth", "age", "gender", "blood_group", "email", "address", "city", "state", "pincode", "marital_status", "occupation"],
    visits: ["patient_name", "patient_phone", "full_name", "contact_number", "visit_date", "chief_complaint", "diagnosis", "notes", "note_status"],
    prescriptions: ["patient_name", "patient_phone", "full_name", "contact_number", "drug_name", "dosage", "frequency", "duration_days", "timing", "instructions", "prescription_date"],
    appointments: ["patient_name", "patient_phone", "full_name", "contact_number", "appointment_date", "appointment_time", "reason", "status"],
  };

  const fetchJobs = useCallback(async () => {
    try {
      const res = await apiRequest<JobInfo[]>("/settings/import/jobs");
      setJobs(Array.isArray(res) ? res : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Refresh job list when a background import finishes
  useEffect(() => {
    if (activeImport?.status === "completed" || activeImport?.status === "failed") {
      fetchJobs();
    }
  }, [activeImport?.status, fetchJobs]);

  // Switch to history step when navigated from the banner View button
  useEffect(() => {
    const state = location.state as { importStep?: Step } | null;
    if (state?.importStep === "history") {
      setStep("history");
    }
  }, [location.state]);

  // Poll job list while any job is still running/pending so the history table
  // always reflects current status (e.g. pending_async → running → completed).
  useEffect(() => {
    const hasActiveJob = jobs.some(j => j.status === "running" || j.status === "pending_async");
    if (!hasActiveJob) return;
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  const fetchMergeProposals = useCallback(async () => {
    setMergeLoading(true);
    try {
      const res = await apiRequest<MergeProposal[]>("/settings/import/merge-proposals?status_filter=pending");
      setMergeProposals(Array.isArray(res) ? res : []);
    } catch { /* ignore */ }
    finally { setMergeLoading(false); }
  }, []);

  const handleMergeAction = async (proposalId: string, action: "approve" | "reject") => {
    setMergeBusyId(proposalId);
    try {
      await apiRequest(`/settings/import/merge-proposals/${proposalId}/${action}`, { method: "POST" });
      toast.success(action === "approve" ? "Patients merged successfully" : "Merge rejected — records kept separate");
      setMergeProposals(prev => prev.filter(p => p.id !== proposalId));
    } catch (e: any) {
      toast.error(e?.message || `Failed to ${action} merge`);
    } finally {
      setMergeBusyId(null);
    }
  };


  const handleZipUpload = async () => {
    if (!zipFile) return;
    setZipUploading(true);
    setZipResult(null);

    // API Gateway hard limit is 10 MB. Files under this threshold are sent
    // directly in the POST body — backend processes them synchronously and
    // returns immediately (works both locally and on staging/prod for small zips).
    // Files over 10 MB skip the body, get a presigned S3 PUT URL back, upload
    // straight to the documents bucket, and Lambda handles async extraction.
    //
    // REACT_APP_ZIP_SYNC_THRESHOLD_MB overrides this for local dev where Lambda
    // is unavailable — set it to a large number (e.g. 9999) to always use sync.
    const thresholdMB = Number(process.env.REACT_APP_ZIP_SYNC_THRESHOLD_MB ?? 10);
    const SYNC_THRESHOLD_BYTES = thresholdMB * 1024 * 1024;
    const useSync = zipFile.size <= SYNC_THRESHOLD_BYTES;

    try {
      const form = new FormData();
      form.append("file_name", zipFile.name);
      if (useSync) {
        form.append("file", zipFile); // backend processes inline, returns upload_url=null
      }

      const resp = await apiFetch(`${API_BASE_URL}/settings/import/upload-zip`, {
        method: "POST",
        body: form,
      });
      if (!resp.ok) {
        throw new Error(await formatApiError(resp, "Failed to start zip upload"));
      }
      const json = await resp.json();
      const { job_id, upload_url } = json.data;

      // Async path (file > 10 MB): PUT zip directly to S3 → Lambda triggers
      if (upload_url) {
        const putResp = await fetch(upload_url, { // external: S3 presigned URL — our headers would break its signature
          method: "PUT",
          headers: { "Content-Type": "application/zip" },
          body: zipFile,
        });
        if (!putResp.ok) {
          throw new Error("Failed to upload file to storage");
        }
        // Hand off to global background tracker — user can navigate away
        trackImport(job_id, "zip", `Processing ${zipFile.name}`);
        toast.info("Zip uploaded — processing in background, you can navigate away");
        setZipFile(null);
        if (zipFileRef.current) zipFileRef.current.value = "";
        setZipUploading(false);
        return;
      }

      setZipFile(null);
      if (zipFileRef.current) zipFileRef.current.value = "";

      // Background thread path: hand off to global tracker (same as presigned URL path)
      trackImport(job_id, "zip", `Processing ${zipFile.name}`);
      toast.info("Zip processing in background — you can navigate away");
      setZipUploading(false);
      return;
    } catch (e: any) {
      toast.error(e.message || "Failed to upload zip bundle");
      setZipUploading(false);
    }
  };

  // Check dependencies when entity changes — cache results to avoid repeated API calls
  const depCacheRef = useRef<Record<string, string | null>>({});
  useEffect(() => {
    const hint = IMPORT_ORDER_HINT[entity];
    if (!hint) {
      setDepWarning(null);
      return;
    }
    // Skip API call if we already checked this entity
    if (entity in depCacheRef.current) {
      setDepWarning(depCacheRef.current[entity]);
      return;
    }
    (async () => {
      try {
        const resp = await apiFetch(`${API_BASE_URL}/settings/import/check-dependencies/${entity}`);
        if (resp.ok) {
          const json = await resp.json();
          const data = json.data;
          const warning = data.ok ? null : data.message;
          depCacheRef.current[entity] = warning;
          setDepWarning(warning);
        }
      } catch {
        setDepWarning(hint);
      }
    })();
  }, [entity]);

  // ── Step 1: Source picker ───────────────────────────────────────────────

  const handleDownloadTemplate = async () => {
    try {
      const resp = await apiFetch(`${API_BASE_URL}/settings/import/templates/${entity}`);
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e.message || "Failed to download template");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum 10 MB.");
      return;
    }
    setFile(f);
  };

  const handleUploadAndMap = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("source_type", source);
      form.append("entity", entity);

      const resp = await apiFetch(`${API_BASE_URL}/settings/import/auto-map`, {
        method: "POST",
        body: form,
      });
      if (!resp.ok) {
        throw new Error(await formatApiError(resp, "Auto-map failed"));
      }
      const json = await resp.json();
      const autoMap: Record<string, string | null> = json.data?.mapping || {};
      setAutoMapping(autoMap);

      const initial: Record<string, string> = {};
      for (const [csvCol, field] of Object.entries(autoMap)) {
        if (field) initial[csvCol] = field;
      }
      setMapping(initial);
      setStep("mapping");
    } catch (e: any) {
      toast.error(e.message || "Failed to auto-map columns");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Column mapping ──────────────────────────────────────────────

  const handleMappingChange = (csvCol: string, kaagazyField: string) => {
    setMapping(prev => {
      const updated = { ...prev };
      if (kaagazyField === "") {
        delete updated[csvCol];
      } else {
        updated[csvCol] = kaagazyField;
      }
      return updated;
    });
  };

  const handleDryRun = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("entity", entity);
      form.append("source_type", source);
      form.append("column_mapping", JSON.stringify(mapping));
      form.append("quick_import", quickImport ? "true" : "false");

      const resp = await apiFetch(`${API_BASE_URL}/settings/import/dry-run`, {
        method: "POST",
        body: form,
      });
      if (!resp.ok) {
        throw new Error(await formatApiError(resp, "Dry run failed"));
      }
      const json = await resp.json();
      setDryResult(json.data);
      setStep("dryrun");
    } catch (e: any) {
      toast.error(e.message || "Dry run failed");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Run import ──────────────────────────────────────────────────

  const handleProceed = async () => {
    if (!file || !dryResult) return;
    setImporting(true);
    setStep("importing");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("job_id", dryResult.job_id);
      form.append("quick_import", quickImport ? "true" : "false");

      const resp = await apiFetch(`${API_BASE_URL}/settings/import/run`, {
        method: "POST",
        body: form,
      });
      if (!resp.ok) {
        throw new Error(await formatApiError(resp, "Import failed"));
      }
      const json = await resp.json();

      // Async path: backend uploaded CSV to S3, Lambda processes it
      if (json.data.status === "pending_async" || json.data.status === "running") {
        const rowCount = dryResult.total_rows.toLocaleString();
        trackImport(json.data.id, "csv", `Importing ${rowCount} ${ENTITY_LABELS[entity].toLowerCase()}`);
        toast.info("Import running in background — you can navigate away");
        setImporting(false);
        setFile(null);
        setDryResult(null);
        setImportResult(null);
        setStep("source");
        return;
      }

      // Sync path: import already completed
      setImportResult(json.data);
      setStep("done");
      setImporting(false);
      fetchJobs();
    } catch (e: any) {
      toast.error(e.message || "Import failed");
      setStep("dryrun");
      setImporting(false);
    }
  };

  const handleReset = () => {
    setStep("source");
    setFile(null);
    setAutoMapping({});
    setMapping({});
    setDryResult(null);
    setImportResult(null);
    setQuickImport(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadErrors = (errors: RowError[]) => {
    const lines = ["Row,Reason", ...errors.map(e => `${e.row},"${e.reason}"`)];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "import_errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* ── Step 1: Source Picker ─────────────────────────────────────── */}
      {step === "source" && (
        <SettingsCard
          title="Import Data"
          subtitle="Bring your clinic's historical data into Kaagazy"
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>}
        >
          <div className="space-y-5">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">What to import</label>
              <div className="flex flex-wrap gap-2">
                {(Object.entries(ENTITY_LABELS) as [Entity, string][]).map(([key, label]) => (
                  <button key={key} onClick={() => setEntity(key)}
                    className={`px-4 py-2 text-xs font-medium rounded-lg border transition-all ${entity === key ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dependency / order warning */}
            {depWarning && (
              <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                <p className="text-xs text-amber-700">{depWarning}</p>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Source</label>
              <div className="flex flex-wrap gap-2">
                {(Object.entries(SOURCE_LABELS) as [Source, string][]).map(([key, label]) => (
                  <button key={key} onClick={() => setSource(key)}
                    className={`px-4 py-2 text-xs font-medium rounded-lg border transition-all ${source === key ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick import toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={quickImport} onChange={e => setQuickImport(e.target.checked)} className="sr-only peer" />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
              </label>
              <div>
                <p className="text-xs font-medium text-gray-700">Quick Import (Demographic Shells)</p>
                <p className="text-[10px] text-gray-400">Only requires name + phone. Useful for cutover day check-ins.</p>
              </div>
            </div>

            <button onClick={handleDownloadTemplate}
              className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3"/></svg>
              Download {ENTITY_LABELS[entity]} Template
            </button>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Upload file</label>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-blue-300 transition-colors cursor-pointer"
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("border-blue-400", "bg-blue-50"); }}
                onDragLeave={e => { e.currentTarget.classList.remove("border-blue-400", "bg-blue-50"); }}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("border-blue-400", "bg-blue-50");
                  const f = e.dataTransfer.files?.[0];
                  if (f) setFile(f);
                }}>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileSelect} />
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
                    <span className="text-sm font-medium text-gray-700">{file.name}</span>
                    <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(0)} KB)</span>
                  </div>
                ) : (
                  <>
                    <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-8m0 0l-3 3m3-3l3 3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3"/></svg>
                    <p className="text-sm text-gray-500">Drag & drop CSV/XLSX here or <span className="text-blue-500 font-medium">click to browse</span></p>
                    <p className="text-xs text-gray-400 mt-1">Max 10 MB</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex gap-3 flex-wrap">
              <button onClick={handleUploadAndMap} disabled={!file || loading}
                className="px-5 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 disabled:opacity-40 transition-opacity">
                {loading ? "Analyzing..." : "Continue"}
              </button>
              <button onClick={() => setStep("history")}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                View Import History
              </button>
            </div>
          </div>
        </SettingsCard>
      )}

      {/* ── Step 2: Column Mapping ───────────────────────────────────── */}
      {step === "mapping" && (
        <SettingsCard
          title="Map Columns"
          subtitle="Match your CSV columns to Kaagazy fields"
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01"/></svg>}
        >
          <div className="space-y-3">
            {quickImport && (
              <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                <p className="text-[11px] text-blue-700">Quick Import mode — only name + phone are required. Other fields are optional and can be backfilled later.</p>
              </div>
            )}

            <div className="hidden sm:grid grid-cols-[1fr_auto_1fr] gap-2 items-center text-xs font-medium text-gray-500 mb-2">
              <span>Your Column</span>
              <span></span>
              <span>Kaagazy Field</span>
            </div>

            {Object.keys(autoMapping).map(csvCol => {
              const currentVal = mapping[csvCol] || "";
              const isAuto = autoMapping[csvCol] !== null;
              return (
                <div key={csvCol} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-1 sm:gap-2 items-center">
                  <span className="text-sm text-gray-700 font-medium truncate">{csvCol}</span>
                  <svg className="hidden sm:block w-4 h-4 text-gray-300" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                  <div className="flex items-center gap-1">
                    <select value={currentVal} onChange={e => handleMappingChange(csvCol, e.target.value)}
                      className={`flex-1 text-base sm:text-sm border rounded-lg px-3 py-2 sm:py-1.5 min-h-[44px] sm:min-h-0 ${currentVal ? "border-emerald-300 bg-emerald-50" : "border-gray-200"}`}>
                      <option value="">-- Skip --</option>
                      {KAAGAZY_FIELDS[entity].map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    {isAuto && <span className="text-[10px] text-emerald-600 font-medium whitespace-nowrap">auto</span>}
                  </div>
                </div>
              );
            })}

            <div className="flex gap-3 pt-4">
              <button onClick={handleDryRun} disabled={loading}
                className="px-5 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 disabled:opacity-40 transition-opacity">
                {loading ? "Validating..." : "Run Validation"}
              </button>
              <button onClick={handleReset}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                Back
              </button>
            </div>
          </div>
        </SettingsCard>
      )}

      {/* ── Step 3: Dry-run Results ──────────────────────────────────── */}
      {step === "dryrun" && dryResult && (
        <SettingsCard
          title="Validation Results"
          subtitle="Review before importing"
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-gray-800">{dryResult.total_rows}</p>
                <p className="text-xs text-gray-500">Total Rows</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{dryResult.valid_rows}</p>
                <p className="text-xs text-emerald-600">Valid</p>
              </div>
              <div className="bg-red-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-red-600">{dryResult.error_rows}</p>
                <p className="text-xs text-red-600">Errors</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{dryResult.duplicate_rows}</p>
                <p className="text-xs text-amber-600">Duplicates</p>
              </div>
            </div>

            {dryResult.errors.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-600">Errors</p>
                  <button onClick={() => downloadErrors(dryResult.errors)}
                    className="text-xs text-blue-500 hover:underline">Download CSV</button>
                </div>
                <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Row</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dryResult.errors.slice(0, 50).map((e, i) => (
                        <tr key={i}>
                          <td className="px-3 py-1.5 text-gray-700 font-mono">{e.row}</td>
                          <td className="px-3 py-1.5 text-red-600">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dryResult.errors.length > 50 && (
                    <p className="text-xs text-gray-400 p-2 text-center">... and {dryResult.errors.length - 50} more</p>
                  )}
                </div>
              </div>
            )}

            {dryResult.dedup_matches && dryResult.dedup_matches.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">
                  Duplicate Matches ({dryResult.dedup_matches.length})
                  <span className="text-gray-400 ml-1">— exact matches auto-merge, others queued for review</span>
                </p>
                <div className="border rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Row</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Import Name</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Existing Name</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Match</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Confidence</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dryResult.dedup_matches.slice(0, 50).map((m, i) => (
                        <tr key={i} className={m.match_reason === "dob+phone+name" ? "bg-emerald-50" : "bg-amber-50"}>
                          <td className="px-3 py-1.5 text-gray-700 font-mono">{m.source_row}</td>
                          <td className="px-3 py-1.5 text-gray-800">{m.source_name}</td>
                          <td className="px-3 py-1.5 text-gray-800">{m.target_name}</td>
                          <td className="px-3 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              m.match_reason === "dob+phone+name"
                                ? "bg-emerald-100 text-emerald-700"
                                : m.match_reason === "dob+phone"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-amber-100 text-amber-700"
                            }`}>
                              {m.match_reason === "dob+phone+name" ? "Auto-merge" : m.match_reason === "dob+phone" ? "DOB+Phone" : "Name+DOB"}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-gray-600">{Math.round(m.confidence * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dryResult.dedup_matches.length > 50 && (
                    <p className="text-xs text-gray-400 p-2 text-center">... and {dryResult.dedup_matches.length - 50} more</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleProceed}
                disabled={dryResult.error_rows > 0 || importing}
                className="px-5 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 hover:opacity-90 disabled:opacity-40 transition-opacity">
                {dryResult.error_rows > 0 ? "Fix errors to proceed" : "Proceed with Import"}
              </button>
              <button onClick={handleReset}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                Fix & Re-upload
              </button>
            </div>
          </div>
        </SettingsCard>
      )}

      {/* ── Step 4: Importing ────────────────────────────────────────── */}
      {step === "importing" && (
        <SettingsCard
          title="Importing Data"
          subtitle="Please don't close this page"
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>}
        >
          <div className="flex flex-col items-center py-8 gap-4">
            <div className="relative w-16 h-16 flex items-center justify-center">
              <svg className="w-16 h-16 animate-spin" viewBox="0 0 50 50" style={{ animationDuration: "1.4s" }}>
                <circle cx="25" cy="25" r="20" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                <circle cx="25" cy="25" r="20" fill="none" stroke="url(#import-grad)" strokeWidth="4"
                  strokeDasharray="80 126" strokeLinecap="round" />
                <defs>
                  <linearGradient id="import-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <p className="text-sm font-semibold text-blue-700">Importing {ENTITY_LABELS[entity]}...</p>
            <p className="text-xs text-gray-400">This may take a few minutes for large files</p>
          </div>
        </SettingsCard>
      )}

      {/* ── Step 5: Done ─────────────────────────────────────────────── */}
      {step === "done" && importResult && (
        <SettingsCard
          title="Import Complete"
          subtitle={`Finished in ${importResult.completed_at && importResult.started_at
            ? ((new Date(importResult.completed_at).getTime() - new Date(importResult.started_at).getTime()) / 1000).toFixed(1) + "s"
            : ""}`}
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-emerald-600">{importResult.created_rows}</p>
                <p className="text-xs text-emerald-600">Created</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{importResult.merged_rows}</p>
                <p className="text-xs text-blue-600">Merged</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{importResult.error_rows}</p>
                <p className="text-xs text-red-600">Failed</p>
              </div>
            </div>

            {/* Next step suggestion */}
            {entity === "patients" && (
              <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                <p className="text-[11px] text-blue-700">Patients imported. You can now import Visits, Prescriptions, and Appointments in that order.</p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleReset}
                className="px-5 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity">
                Import More Data
              </button>
              <button onClick={() => setStep("history")}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                View History
              </button>
            </div>
          </div>
        </SettingsCard>
      )}

      {/* ── Import History ────────────────────────────────────────────── */}
      {step === "history" && (
        <SettingsCard
          id="import-history"
          title="Import History"
          subtitle="Past import jobs for your clinic"
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
          headerRight={
            <button onClick={() => { handleReset(); setStep("source"); }}
              className="text-xs text-blue-500 hover:underline font-medium">
              New Import
            </button>
          }
        >
          {jobs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No imports yet</p>
          ) : (
            <div className="border rounded-lg overflow-hidden overflow-x-auto">
              <table className="w-full text-xs" style={{ minWidth: 480 }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Date</th>
                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Entity</th>
                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Source</th>
                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Rows</th>
                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {jobs.map(j => (
                    <tr key={j.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-700">{fmtDate(j.created_at)}</td>
                      <td className="px-3 py-2 text-gray-700 capitalize">{j.entity}</td>
                      <td className="px-3 py-2 text-gray-500">{SOURCE_LABELS[j.source_type as Source] || j.source_type}</td>
                      <td className="px-3 py-2 text-gray-700">{j.total_rows}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[j.status] || "bg-gray-100 text-gray-500"}`}>
                          {j.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SettingsCard>
      )}

      {/* ── Merge Review ──────────────────────────────────────────── */}
      {step === "merge-review" && (
        <SettingsCard
          title="Merge Review"
          subtitle="Review proposed patient duplicates — approve to merge, reject to keep separate"
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>}
          headerRight={
            <button onClick={() => { handleReset(); setStep("source"); }}
              className="text-xs text-blue-500 hover:underline font-medium">Back to Import</button>
          }
        >
          {mergeLoading ? (
            <p className="text-sm text-gray-400 text-center py-6">Loading proposals...</p>
          ) : mergeProposals.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No pending merge proposals</p>
              <p className="text-xs text-gray-400 mt-1">All duplicates have been reviewed or auto-merged</p>
            </div>
          ) : (
            <div className="space-y-3">
              {mergeProposals.map(p => (
                <div key={p.id} className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                      p.match_reason === "dob+phone" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                    }`}>
                      {p.match_reason === "dob+phone" ? "DOB + Phone" : "Name + DOB"}
                      <span className="ml-1">{Math.round(p.confidence * 100)}%</span>
                    </span>
                    <span className="text-[10px] text-gray-400">{fmtDate(p.created_at)}</span>
                  </div>

                  {/* Side-by-side diff */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-red-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-red-500 mb-1.5">Imported (will be deactivated)</p>
                      {p.source_snapshot && (
                        <div className="space-y-1 text-xs text-gray-700">
                          <p><span className="text-gray-400">Name:</span> {p.source_snapshot.first_name} {p.source_snapshot.last_name}</p>
                          <p><span className="text-gray-400">Phone:</span> {p.source_snapshot.contact_number || "—"}</p>
                          <p><span className="text-gray-400">DOB:</span> {p.source_snapshot.date_of_birth || "—"}</p>
                          <p><span className="text-gray-400">Gender:</span> {p.source_snapshot.gender || "—"}</p>
                        </div>
                      )}
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-emerald-500 mb-1.5">Existing (will be kept)</p>
                      {p.target_snapshot && (
                        <div className="space-y-1 text-xs text-gray-700">
                          <p><span className="text-gray-400">Name:</span> {p.target_snapshot.first_name} {p.target_snapshot.last_name}</p>
                          <p><span className="text-gray-400">Phone:</span> {p.target_snapshot.contact_number || "—"}</p>
                          <p><span className="text-gray-400">DOB:</span> {p.target_snapshot.date_of_birth || "—"}</p>
                          <p><span className="text-gray-400">Gender:</span> {p.target_snapshot.gender || "—"}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => handleMergeAction(p.id, "reject")}
                      disabled={mergeBusyId === p.id}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                      Keep Separate
                    </button>
                    <button
                      onClick={() => handleMergeAction(p.id, "approve")}
                      disabled={mergeBusyId === p.id}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-colors">
                      {mergeBusyId === p.id ? "Merging..." : "Approve Merge"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SettingsCard>
      )}

      {step !== "history" && step !== "source" && step !== "merge-review" && (
        <div className="flex gap-3">
          <button onClick={() => setStep("history")}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
            View import history
          </button>
          <button onClick={() => { fetchMergeProposals(); setStep("merge-review"); }}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
            Review merge proposals
          </button>
        </div>
      )}

      {/* ── Zip Bundle Upload (always visible) ───────────────────────── */}
      <SettingsCard
        title="Upload Document Bundle"
        subtitle="Import prescription PDFs, X-rays, and lab reports from a zip file"
        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>}
      >
        <div className="space-y-4">
          {/* Filename format hint */}
          <div className="border border-blue-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-blue-50 flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <p className="text-[11px] font-semibold text-blue-700">How to prepare your zip</p>
            </div>
            <div className="px-3 py-2.5 space-y-2.5 bg-white text-[11px] text-gray-700">
              {/* Step 1 */}
              <div className="flex gap-2">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center">1</span>
                <div>
                  <p className="font-medium text-gray-800">Create folders by document type</p>
                  <p className="text-gray-500 mt-0.5">Allowed names: <span className="font-mono">prescriptions · xray · lab · images · scan · reports</span></p>
                </div>
              </div>
              {/* Step 2 */}
              <div className="flex gap-2">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center">2</span>
                <div>
                  <p className="font-medium text-gray-800">Name each file: <span className="font-mono text-blue-700">patientphone_YYYY-MM-DD.ext</span></p>
                  <p className="text-gray-500 mt-0.5">e.g. <span className="font-mono">9876543210_2024-03-15.pdf</span> or <span className="font-mono">9876543210_2024_03_15.pdf</span></p>
                  <p className="text-gray-500">Accepted extensions: <span className="font-mono">.pdf · .png · .jpg · .jpeg · .dicom</span></p>
                </div>
              </div>
              {/* Step 3 */}
              <div className="flex gap-2">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center">3</span>
                <div>
                  <p className="font-medium text-gray-800">Select all folders → right-click → compress/zip</p>
                </div>
              </div>
              {/* Structure preview */}
              <div className="font-mono text-[10px] bg-gray-50 border border-gray-100 rounded px-2.5 py-2 space-y-0.5 leading-relaxed text-gray-600">
                <p>documents.zip</p>
                <p>&nbsp;├─ prescriptions/</p>
                <p>&nbsp;│&nbsp;&nbsp;&nbsp;└─ 9876543210_2024-03-15.pdf</p>
                <p>&nbsp;├─ xray/</p>
                <p>&nbsp;│&nbsp;&nbsp;&nbsp;└─ 9876543210_2024-03-15.png</p>
                <p>&nbsp;└─ lab/</p>
                <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└─ 9876543210_2024-03-15.pdf</p>
              </div>
              <p className="text-amber-600 text-[10px]">⚠ Files placed directly in the zip root (not inside a folder) will be rejected.</p>
            </div>
          </div>

          {/* Drop zone */}
          <div
            className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-purple-300 transition-colors cursor-pointer"
            onClick={() => zipFileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("border-purple-400", "bg-purple-50"); }}
            onDragLeave={e => { e.currentTarget.classList.remove("border-purple-400", "bg-purple-50"); }}
            onDrop={e => {
              e.preventDefault();
              e.currentTarget.classList.remove("border-purple-400", "bg-purple-50");
              const f = e.dataTransfer.files?.[0];
              if (f) { setZipFile(f); setZipResult(null); }
            }}
          >
            <input
              ref={zipFileRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) { setZipFile(f); setZipResult(null); } }}
            />
            {zipFile ? (
              <div className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
                <span className="text-sm font-medium text-gray-700">{zipFile.name}</span>
                <span className="text-xs text-gray-400">({(zipFile.size / 1024 / 1024).toFixed(1)} MB)</span>
              </div>
            ) : (
              <>
                <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                <p className="text-sm text-gray-500">Drag & drop a <span className="font-medium">.zip</span> file here or <span className="text-purple-500 font-medium">click to browse</span></p>
                <p className="text-xs text-gray-400 mt-1">Max 100 MB</p>
              </>
            )}
          </div>

          {/* Processing indicator */}
          {zipUploading && !zipResult && (
            <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
              <svg className="w-5 h-5 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
              <div>
                <p className="text-sm font-medium text-blue-700">Processing zip bundle...</p>
                <p className="text-xs text-blue-500">Extracting files and matching to patients</p>
              </div>
            </div>
          )}

          {/* Result summary */}
          {zipResult && (
            <div className="space-y-3">
              {zipResult.validation_errors?.length ? (
                /* ── Validation errors: hard stop ── */
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-red-50">
                    <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    <p className="text-sm font-medium text-red-700">Zip structure invalid — nothing was imported</p>
                  </div>
                  <ul className="divide-y divide-red-100">
                    {zipResult.validation_errors.map((err, i) => (
                      <li key={i} className="px-4 py-2.5 text-xs text-red-700 flex items-start gap-2">
                        <span className="mt-0.5 text-red-400 flex-shrink-0">✕</span>
                        {err}
                      </li>
                    ))}
                  </ul>
                  <div className="px-4 py-2 bg-red-50 border-t border-red-100">
                    <p className="text-[11px] text-red-500">Fix the zip file and upload again.</p>
                  </div>
                </div>
              ) : zipResult.status === "failed" ? (
                /* ── Generic failure ── */
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                  <p className="text-sm text-red-700">Zip processing failed. Check the file and try again.</p>
                </div>
              ) : (
                /* ── Success/partial stats ── */
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-emerald-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{zipResult.processed}</p>
                      <p className="text-xs text-emerald-600">Imported</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-amber-600">{zipResult.skipped}</p>
                      <p className="text-xs text-amber-600">Skipped</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-red-600">{zipResult.failed}</p>
                      <p className="text-xs text-red-600">Failed</p>
                    </div>
                  </div>

                  {zipResult.skipped > 0 && (
                    <p className="text-[11px] text-amber-600">
                      ⚠ {zipResult.skipped} file(s) skipped — phone number in filename not found in patient records.
                    </p>
                  )}

                  {/* Per-file failure reasons */}
                  {zipResult.failed > 0 && zipResult.validation_errors && zipResult.validation_errors.length > 0 && (
                    <div className="border border-red-200 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-2 bg-red-50">
                        <svg className="w-3.5 h-3.5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        <p className="text-[11px] font-semibold text-red-700">{zipResult.failed} file(s) failed — reasons below</p>
                      </div>
                      <ul className="divide-y divide-red-100">
                        {zipResult.validation_errors.map((err, i) => (
                          <li key={i} className="px-3 py-2 text-[11px] text-red-700 flex items-start gap-2 font-mono">
                            <span className="text-red-400 flex-shrink-0 mt-0.5">✕</span>
                            {err}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {zipResult.failed > 0 && (!zipResult.validation_errors || zipResult.validation_errors.length === 0) && (
                    <p className="text-[11px] text-red-600">
                      ✕ {zipResult.failed} file(s) failed during processing. Check server logs for details.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleZipUpload}
            disabled={!zipFile || zipUploading}
            className="px-5 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center gap-2"
          >
            {zipUploading && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            )}
            {zipUploading ? "Processing..." : "Upload & Extract"}
          </button>
        </div>
      </SettingsCard>
    </div>
  );
};
